import React, { useState, useEffect, useRef } from 'react';
import './App.scss';
import { Filters } from './components/Filters';
import { Movies } from './components/Movies';
import { Tabs } from './components/Tabs';
import { Popup } from './components/Popup';
import { LocationOptions } from './components/LocationOptions';
import { getMapiDataRequestURL, getMapiData, getMapiCache, setMapiCache } from 'utils/mapiData'

const App = (props) => {
  const { 
    dataUrl,
    isPopup,
    singleCinema,
    homeURL,
    useSiteAsFilter,
    filters,
    hideFilters,
    colorScheme,
    movieName,
    singleMovie,
    showAllMovies,
    showSearch,
    concessionBtnText,
    concessionSiteLink,
    concessionLinkNewTab,
    concessionEnabledParam,
    mapiCacheExpiration,
    disableCache,
    testCinemasEnabled,
    initialData
  } = props;

  // set pre-defined filters
  const initialSelectedFilters = {
    cinema: [],
    movie: [],
    attrs: [],
    genre: [],
    rating: [],
    other: [],
  };

  if (filters) {
    Object.keys(filters).forEach(filter => {
      if (filters[filter] && filters[filter].length && initialSelectedFilters[ filter ]) {
        initialSelectedFilters[ filter ] = filters[filter];
      }
    });
  }

  const [activeTab, setActiveTab] = useState(window.mapiTranslations['Choose Film']);
  const [isLoading, setIsLoading] = useState(!initialData);
  const [sites, setSites] = useState([]);
  const [movies, setMovies] = useState([]);
  const [comingSoonMovies, setComingSoonMovies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [areFilterOptionsSet, setAreFilterOptionsSet] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    sites: [],
    movies: [],
    attrs: [],
    genre: [],
    rating: [],
    other: [
      {
        label: 'Dubbed',
        value: 'dubbed',
        moviesCount: 0,
        filter: 'other'
      },
      {
        label: 'Subtitled',
        value: 'subtitled',
        moviesCount: 0,
        filter: 'other'
      }
    ],
  });
  const [selectedFilters, setSelectedFilters] = useState(initialSelectedFilters);
  const skipNextFetchRef = useRef(false);

  useEffect(() => {
    if (initialData) {
      handleMapiData(initialData);
      setIsLoading(false);
      skipNextFetchRef.current = true;
    }
  }, [initialData]);

  // initial and filter is changed call
  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    getAppData();
  }, [selectedFilters]);

  // handle movie list update
  useEffect(() => {
    if (movies.length && ! areFilterOptionsSet) {
      let attrsOptions = [];
      let catsOptions = [];
      let ratingOptions = [];
      let moviesOptions = [];
      let isDubbedCounter = 0;
      let isSubtitledCounter = 0;

      // collect filter options
      movies.forEach(movie => {
        attrsOptions = attrsOptions.concat(movie['attrs']);
        catsOptions = catsOptions.concat(movie['cats']);
        ratingOptions = ratingOptions.concat(movie['ratingName']);

        moviesOptions.push({
          value: movie['edi'],
          label: movie['webName'],
          moviesCount: 1,
          filter: 'movie'
        });

        // update dubbed/subtitled counters
        if (movie['isDubbed'] === '1') {
          isDubbedCounter++;
        }

        if (movie['isSubtitled'] === '1') {
          isSubtitledCounter++;
        }
      });
      
      // get unique formatted values
      attrsOptions = getFormattedUniqueValues(attrsOptions, 'attrs');
      catsOptions = getFormattedUniqueValues(catsOptions, 'genre');
      ratingOptions = getFormattedUniqueValues(ratingOptions, 'rating');

      // prepare all filters
      const options = {
        ...filterOptions,
        attrs: attrsOptions,
        genre: catsOptions,
        rating: ratingOptions,
        movies: moviesOptions,
        other: []
      };

      // add if only there are any matches
      if (isDubbedCounter > 0) {
        options.other.push({
          label: 'Dubbed',
          value: 'dubbed',
          moviesCount: isDubbedCounter,
          filter: 'other'
        });
      }

      // add if only there are any matches
      if (isSubtitledCounter > 0) {
        options.other.push({
          label: 'Subtitled',
          value: 'subtitled',
          moviesCount: isSubtitledCounter,
          filter: 'other'
        });
      }

      // set filter options
      setFilterOptions(options);

      setAreFilterOptionsSet(true);
    }
  }, [movies]);

  const getFormattedUniqueValues = (arrayData, filter) => {
    const valueCounters = {};

    arrayData = arrayData.filter((item, pos) => {
      if (item) {
        if (!valueCounters[item]) {
          valueCounters[item] = 1;
        } else {
          valueCounters[item]++;
        }
      }

      if (!item) {
        return false;
      }
      
      return arrayData.indexOf(item) === pos
    });
    
    arrayData = formatArrayValuesAsOptions(arrayData, filter, valueCounters);

    return arrayData;
  }

  const formatArrayValuesAsOptions = (arrayData, filterName, valueCounters) => {
    arrayData = arrayData.map(item => { 
      return { 
        value: item, 
        label: item,
        moviesCount: valueCounters[item],
        filter: filterName
      }; 
    });

    return arrayData;
  }

  const setFilter = (filter, value, action) => {
    if (action === 'add') {
      selectedFilters[filter].push(value);
    } else if (action === 'replace') {
      selectedFilters[filter] = value;
    } else {
      const valueIndex = selectedFilters[filter].indexOf(value);
      selectedFilters[filter].splice(valueIndex, 1);
    }

    setAreFilterOptionsSet(false);
    setSelectedFilters({...selectedFilters});    
  }

  const resetFilters = (filter) => {
    if (filter) {
      selectedFilters[filter] = [];
    } else {
      selectedFilters.cinema = [];
      selectedFilters.movie = [];
      selectedFilters.attrs = [];
      selectedFilters.genre = [];
      selectedFilters.rating = [];
      selectedFilters.other = [];
    }

    setAreFilterOptionsSet(false);
    setSelectedFilters({...selectedFilters});
  }

  const handleMapiData = (data) => {
    const sitesData = data.sites || [];
    const moviesData = data.movies || [];
    const comingSoonMoviesData = data.comingSoonMovies || [];
    const groups = Object.values(data.groups) || [];

    setSites(sitesData);
  
    setMovies(moviesData);
    
    setComingSoonMovies(comingSoonMoviesData);

    setGroups(groups);

    // fix movie browser background image layout issue
    const mapiWrapper = document.querySelector('#mapi-wrapper');
    if (mapiWrapper) {
      if (moviesData?.length <= 8) {
        mapiWrapper.classList.add('has-less-movies')
      } else {
        mapiWrapper.classList.remove('has-less-movies')
      }
    }
  }

  const getAppData = async () => {
    const mapiDataRequestURL = await getMapiDataRequestURL(dataUrl, singleCinema, singleMovie, useSiteAsFilter, selectedFilters, testCinemasEnabled, concessionEnabledParam);

    if (!disableCache) {
      const mapiCacheData = getMapiCache(mapiDataRequestURL.filters);

      if (mapiCacheData) {
        handleMapiData(mapiCacheData);
        setIsLoading(false);
        return;
      }
    }

    // let requestURL = dataUrl + '?action=get_sites_data&security=' + ajaxNonce + '&single_cinema=' + singleCinema;

    // Object.keys(selectedFilters).forEach(filter => {
    //   requestURL += '&' + filter + '=' + selectedFilters[filter].join(',');
    // });

    // // handle experience page - add flag
    // requestURL += '&isExperiencePage=' + useSiteAsFilter;

    // // add is single movie flag
    // requestURL += '&single_movie=' + singleMovie;

    // await axios.get(requestURL + '&isPopup=' +  isPopup)
    //   .then(jsonResponse => {
    //     // cache the response so the popup instance will use it
    //     // reduce the API calls
    //     if (!isPopup) {
    //       sessionStorage.setItem('mapi-data', JSON.stringify(jsonResponse.data));
    //     }
        
    //     handleMapiData(jsonResponse.data);

    //     setIsLoading(false);
    //   });

    getMapiData(mapiDataRequestURL.full).then(response => {
      if (!disableCache) {
        setMapiCache(mapiDataRequestURL.filters, response, mapiCacheExpiration);
      }

      handleMapiData(response);

      setIsLoading(false);
    });
  }

  return (
    <div className="movie-browser-wrapper lightTheme">
      {!isPopup &&
        <Tabs
          useSiteAsFilter={useSiteAsFilter}
          setActiveTab={setActiveTab}
          activeTab={activeTab}
          colorScheme={colorScheme}
        > 
          <div label={window.mapiTranslations['Choose Film']}> 
            {
              !hideFilters &&
              <Filters 
                sites={sites} 
                filterOptions={filterOptions} 
                isSearchable={true} 
                isRtl={false} 
                homeURL={homeURL} 
                setFilter={setFilter}
                resetFilters={resetFilters}
                selectedFilters={selectedFilters}
                useSiteAsFilter={useSiteAsFilter}
                colorScheme={colorScheme}
              />
            }
            
            
            <Movies 
              movies={movies} 
              homeURL={homeURL} 
              selectedFilters={selectedFilters} 
              showAllMovies={showAllMovies} 
              concessionEnabledParam={concessionEnabledParam} 
              testCinemasEnabled={testCinemasEnabled} 
            />
          </div>

          {singleCinema === '0' &&
            <div label={window.mapiTranslations['Choose Cinema']}> 
              <LocationOptions 
                cinemas={sites} 
                setFilter={setFilter}
                isPopup={isPopup}
                homeURL={homeURL}
                useSiteAsFilter={useSiteAsFilter}
                setActiveTab={setActiveTab}
                colorScheme={colorScheme}
                showSearch={showSearch}
                concessionBtnText={concessionBtnText}
                concessionSiteLink={concessionSiteLink}
                concessionLinkNewTab={concessionLinkNewTab}
                concessionEnabledParam={concessionEnabledParam}
                testCinemasEnabled={testCinemasEnabled} 
              />
            </div>
          }

          <div label={window.mapiTranslations['Coming Soon']}> 
            <Movies 
              movies={comingSoonMovies} 
              homeURL={homeURL} 
              selectedFilters={selectedFilters} 
              isComingSoon={true} 
              showAllMovies={showAllMovies} 
              concessionEnabledParam={concessionEnabledParam} 
              testCinemasEnabled={testCinemasEnabled} 
            />
          </div>

          {groups.length > 0 &&
            groups.map(group => {
              return (
                <div key={group.code} label={group.name}>
                  <Movies
                    movies={group.movies}
                    homeURL={homeURL} 
                    selectedFilters={selectedFilters} 
                    isComingSoon={false} 
                    showAllMovies={showAllMovies} 
                    concessionEnabledParam={concessionEnabledParam} 
                  />
                </div>
              );
            })
          }
        </Tabs>
      }

      {isPopup &&
        <Popup 
          sites={sites} 
          setFilter={setFilter}
          homeURL={homeURL}
          colorScheme={colorScheme}
          movieName={movieName}
          showSearch={showSearch}
          concessionBtnText={concessionBtnText}
          concessionSiteLink={concessionSiteLink}
          concessionLinkNewTab={concessionLinkNewTab}
          concessionEnabledParam={concessionEnabledParam}
        />
      }
    </div>
  );
}

export default App;
